import { Component } from '@theme/component';

const STORAGE_KEY = 'pecheneyes:giveaway';

/**
 * @typedef {object} GiveawayPopupRefs
 * @property {HTMLDialogElement} dialog - The modal.
 * @property {HTMLElement} teaser - Corner teaser shown while the modal is closed.
 * @property {HTMLElement} [success] - Success screen, present after a successful sign-up post.
 * @property {HTMLElement} [code] - The discount code text.
 * @property {HTMLElement} [status] - Live status for the copy action.
 */

/** @returns {{ closed?: boolean, joined?: boolean, teaserHidden?: boolean }} */
function readState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

/** @param {{ closed?: boolean, joined?: boolean, teaserHidden?: boolean }} patch */
function writeState(patch) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readState(), ...patch }));
  } catch {
    // Storage can be unavailable (private mode); the popup then simply shows again next visit.
  }
}

/** @param {string} event @param {Record<string, unknown>} [data] */
function track(event, data = {}) {
  const w = /** @type {any} */ (window);
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event, ...data });
}

/**
 * Giveaway / welcome-discount popup with exit intent and a corner teaser.
 *
 * @extends Component<GiveawayPopupRefs>
 */
class GiveawayPopup extends Component {
  requiredRefs = ['dialog', 'teaser'];

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const state = readState();

    this.refs.dialog.addEventListener('close', () => this.#onClose(), { signal });

    // Returning from a successful sign-up post: show the code right away.
    if (this.refs.success) {
      if (!state.joined) track('pecheneyes_giveaway_joined');
      writeState({ joined: true, closed: true });
      this.open();
      return;
    }

    if (location.hash === '#AtelierGiveawayForm') {
      // A post with errors returns to the anchor; reopen so the message is visible.
      this.open();
      return;
    }

    if (state.joined) return;
    // The badge is the resting state; the card only opens itself when auto-open is enabled.
    this.#showTeaser();
    if (state.closed || this.dataset.autoOpen !== 'true') {
      this.#watchExitIntent(signal);
      return;
    }

    const delay = Number(this.dataset.delay) || 6000;
    this.#timer = window.setTimeout(() => this.#autoOpen(), delay);
    this.#watchExitIntent(signal);
  }

  /** @param {AbortSignal} signal */
  #watchExitIntent(signal) {
    if (this.dataset.exitIntent !== 'true' || !matchMedia('(pointer: fine)').matches || readState().closed) return;
    document.addEventListener('mouseout', (event) => {
      if (!event.relatedTarget && event.clientY <= 0) this.#autoOpen();
    }, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    window.clearTimeout(this.#timer);
  }

  open() {
    const { dialog, teaser } = this.refs;
    window.clearTimeout(this.#timer);
    teaser.hidden = true;
    if (dialog.open) return;
    // Non-modal: the page stays usable while the card is open.
    dialog.show();
    const gsap = /** @type {any} */ (window).gsap;
    if (gsap && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Unfold from the badge's corner.
      gsap.fromTo(
        dialog,
        { autoAlpha: 0, scale: 0.55, y: 18, transformOrigin: document.dir === 'rtl' ? 'bottom right' : 'bottom left' },
        { autoAlpha: 1, scale: 1, y: 0, duration: 0.6, ease: 'expo.out', clearProps: 'transform,opacity,visibility' }
      );
    }
  }

  close() {
    const { dialog } = this.refs;
    const gsap = /** @type {any} */ (window).gsap;
    if (gsap && dialog.open && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gsap.to(dialog, {
        autoAlpha: 0, scale: 0.6, y: 12, duration: 0.3, ease: 'power2.in',
        transformOrigin: document.dir === 'rtl' ? 'bottom right' : 'bottom left',
        onComplete: () => { dialog.close(); gsap.set(dialog, { clearProps: 'transform,opacity,visibility' }); },
      });
      return;
    }
    dialog.close();
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  hideTeaser() {
    this.refs.teaser.hidden = true;
    writeState({ teaserHidden: true });
  }

  /** @param {MouseEvent} event */
  async copyAndApply(event) {
    const code = this.refs.code?.textContent?.trim();
    const link = event.target instanceof Element ? event.target.closest("a") : null;
    if (!code || !link) return;

    event.preventDefault();
    try {
      await navigator.clipboard.writeText(code);
      if (this.refs.status) this.refs.status.textContent = this.dataset.copiedLabel ?? '';
    } catch {
      // Clipboard can be blocked; applying the code below still works.
    }
    track('pecheneyes_discount_applied', { code });
    window.setTimeout(() => location.assign(link.href), 600);
  }

  #autoOpen() {
    const state = readState();
    if (state.closed || state.joined || this.refs.dialog.open) return;
    // Opening on its own must not pull focus away from what the shopper is doing.
    const previous = document.activeElement;
    this.open();
    if (previous instanceof HTMLElement && previous !== document.body) previous.focus({ preventScroll: true });
    else if (document.activeElement instanceof HTMLElement && this.refs.dialog.contains(document.activeElement)) document.activeElement.blur();
  }

  #onClose() {
    const state = readState();
    writeState({ closed: true });
    if (!state.joined && !this.refs.success && !state.teaserHidden) this.#showTeaser();
  }

  #showTeaser() {
    if (readState().teaserHidden) return;
    this.refs.teaser.hidden = false;
  }
}

if (!customElements.get('giveaway-popup-component')) {
  customElements.define('giveaway-popup-component', GiveawayPopup);
}
